import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings2, X, Copy, GripVertical, AlertCircle, BarChart3, ExternalLink,
} from 'lucide-react';
import ChartRenderer from '../../../analytics-shared/components/ChartRenderer';
import { formatValue } from '../../../analytics-shared/engine/reportEngine';
import { isTableChart } from '../../../analytics-shared/engine/widgetSchema';
import { useWidgetData } from '../../hooks/useDashboards';

const WidgetSkeleton = () => (
  <div className="flex flex-col h-full p-4 gap-3 animate-pulse">
    <div className="h-4 bg-slate-100 rounded-lg w-3/5" />
    <div className="h-3 bg-slate-100 rounded-lg w-2/5" />
    <div className="flex-1 bg-slate-100 rounded-xl" />
  </div>
);

// Lightweight table renderer for chartType==='table'. Uses the column list
// resolved by useWidgetData (explicit widget columns → report columns →
// first 5 fields). formatValue makes currency/date columns render with
// the right symbols.
const WidgetTable = ({ records, columns, fieldMap, height }) => {
  if (!columns || !columns.length) {
    return (
      <div className="flex items-center justify-center h-full text-[11px] text-slate-400 px-3 text-center">
        No columns configured.
      </div>
    );
  }
  if (!records || records.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-[11px] text-slate-400">
        No records match this widget's filters.
      </div>
    );
  }
  return (
    <div className="overflow-auto" style={{ maxHeight: height }}>
      <table className="w-full text-[11px] border-collapse">
        <thead className="sticky top-0 bg-slate-50 z-10">
          <tr>
            {columns.map((c) => (
              <th key={c} className="px-2 py-1.5 text-left font-semibold text-slate-600 border-b border-slate-200 whitespace-nowrap">
                {(fieldMap[c] || {}).label || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.slice(0, 200).map((rec, idx) => (
            <tr key={rec.id || idx} className="border-b border-slate-50 hover:bg-slate-50">
              {columns.map((c) => (
                <td key={c} className="px-2 py-1 text-slate-700 whitespace-nowrap">
                  {formatValue(rec[c], fieldMap[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {records.length > 200 && (
        <p className="text-[10px] text-slate-400 px-2 py-1.5 italic">
          Showing first 200 of {records.length} rows.
        </p>
      )}
    </div>
  );
};

const DashboardWidget = ({ widget, editMode = false, onRemove, onConfigure, onDuplicate, onDrillDown }) => {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const chartContainerRef = useRef(null);
  const [chartH, setChartH] = useState(80);

  useEffect(() => {
    const el = chartContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setChartH(Math.max(40, entry.contentRect.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const { data: widgetData, isLoading } = useWidgetData(widget);
  const chartData = widgetData?.data;
  const report = widgetData?.report;
  const chartDef = widgetData?.chartDef;
  const error = widgetData?.error;
  const unconfigured = widgetData?.unconfigured;

  const chartType = widget.chartType || chartDef?.type || 'bar';
  const showHeader = widget.config?.showTitle !== false;
  const isTable = isTableChart(chartType);
  // Pull the engine-known field map + records out for the table renderer.
  const tableRecords = widgetData?.records || [];
  const tableColumns = widgetData?.tableColumns || [];
  const fieldMap = widgetData?.fieldMap || {};

  const handleDrillDown = (category) => {
    if (onDrillDown && widget.config?.drillDownEnabled !== false && !editMode) {
      onDrillDown({ widget, report, category });
    }
  };

  return (
    <div
      className={`relative flex flex-col h-full bg-white rounded-xl border transition-all duration-200 overflow-hidden select-none
        ${editMode
          ? 'border-slate-200 shadow-[0_2px_8px_rgba(0,0,0,0.06)] hover:border-indigo-300 hover:shadow-[0_4px_16px_rgba(99,102,241,0.12)]'
          : 'border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]'}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {editMode && (
        <div className="widget-drag-handle absolute top-0 left-0 right-0 h-5 flex items-center justify-center cursor-grab active:cursor-grabbing bg-gradient-to-b from-slate-100/80 to-transparent z-10 hover:from-indigo-100/70 transition-colors">
          <GripVertical className="w-4 h-4 text-slate-300" />
        </div>
      )}

      {editMode && hovered && (
        <div className="absolute top-1.5 right-1.5 flex items-center gap-1 z-20">
          {onConfigure && (
            <button onClick={(e) => { e.stopPropagation(); onConfigure(widget); }}
              className="p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-indigo-50 hover:border-indigo-300 transition-all" title="Configure">
              <Settings2 className="w-3 h-3 text-slate-500" />
            </button>
          )}
          {onDuplicate && (
            <button onClick={(e) => { e.stopPropagation(); onDuplicate(widget); }}
              className="p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-indigo-50 transition-all" title="Duplicate">
              <Copy className="w-3 h-3 text-slate-500" />
            </button>
          )}
          {onRemove && (
            <button onClick={(e) => { e.stopPropagation(); onRemove(widget.id); }}
              className="p-1.5 bg-white border border-slate-200 rounded-lg shadow-sm hover:bg-red-50 hover:border-red-300 transition-all" title="Remove">
              <X className="w-3 h-3 text-red-500" />
            </button>
          )}
        </div>
      )}

      {showHeader && (
        <div className={`px-3 flex-shrink-0 ${editMode ? 'pt-6 pb-1' : 'pt-3 pb-1'}`}>
          <h3 className="text-xs font-bold text-slate-800 truncate leading-snug">{widget.title}</h3>
          {widget.subtitle && (
            <p className="text-[10px] text-slate-400 mt-0.5 truncate">{widget.subtitle}</p>
          )}
        </div>
      )}

      <div ref={chartContainerRef} className="flex-1 min-h-0 overflow-hidden px-1.5 pb-1">
        {error ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-slate-400 py-4">
            <AlertCircle className="w-7 h-7 text-red-200" />
            <span className="text-[10px] text-center px-2 text-slate-400 leading-relaxed">{error}</span>
          </div>
        ) : isLoading ? (
          <WidgetSkeleton />
        ) : isTable ? (
          <WidgetTable records={tableRecords} columns={tableColumns} fieldMap={fieldMap} height={chartH} />
        ) : unconfigured || !chartData ? (
          <div className="flex items-center justify-center h-full px-3 text-center text-[11px] text-slate-400 leading-relaxed">
            Pick a field for the {chartType === 'kpi' ? 'measure' : 'axes'} to render this chart.
          </div>
        ) : (
          <ChartRenderer
            type={chartType}
            data={chartData}
            height={chartH}
            valueLabel={widget.config?.yAxisLabel || chartDef?.title || 'Value'}
            xAxisLabel={widget.config?.xAxisLabel}
            yAxisLabel={widget.config?.yAxisLabel}
            showLegend={widget.config?.showLegend !== false}
            showGrid={widget.config?.showGrid !== false}
            showTooltip={widget.config?.showTooltip !== false}
            onSliceClick={!editMode ? handleDrillDown : undefined}
          />
        )}
      </div>

      {(report || widget.reportId) && (
        <ReportFooterLink
          reportId={widget.reportId}
          reportName={report?.name}
          onNavigate={() => {
            // useWidgetData stores the resolved report; widget.reportId is the
            // authoritative pointer. Falling back to widget.reportId means the
            // link still works during the initial load before useWidgetData
            // resolves, and after a saved report rename.
            const id = widget.reportId || report?.id;
            if (id) navigate(`/reports/view/${id}`);
          }}
        />
      )}
    </div>
  );
};

/**
 * Salesforce-style "go to source report" footer. Lives at the bottom of
 * every widget regardless of chart kind, so clicking through to the
 * underlying Report view is the same gesture for bars, pies, KPIs,
 * tables, and funnels.
 *
 * Clicks stop propagation so the widget's own click handlers (selection
 * in edit mode, drill-down in view mode) don't fire at the same time —
 * navigating away while also selecting the widget would be jarring.
 */
const ReportFooterLink = ({ reportId, reportName, onNavigate }) => {
  if (!reportId) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onNavigate(); }}
      onMouseDown={(e) => e.stopPropagation()}
      className="group/footer w-full px-3 pb-2 pt-1 flex items-center gap-1.5 flex-shrink-0 border-t border-slate-50 cursor-pointer hover:bg-indigo-50/60 transition-colors"
      title={reportName ? `Open "${reportName}" in Report Builder` : 'Open source report'}
    >
      <BarChart3 className="w-2.5 h-2.5 text-slate-300 group-hover/footer:text-indigo-500 flex-shrink-0 transition-colors" />
      <span className="text-[9px] text-slate-400 group-hover/footer:text-indigo-600 group-hover/footer:underline truncate transition-colors">
        {reportName || 'Open source report'}
      </span>
      <ExternalLink className="w-2.5 h-2.5 text-slate-300 group-hover/footer:text-indigo-500 flex-shrink-0 opacity-0 group-hover/footer:opacity-100 transition-all" />
    </button>
  );
};

export default DashboardWidget;
